import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { getSettings, updateSetting, updateSettings } from '../../api/admin/adminSettings';


export const useAdminSettings = () => {
  const { setError } = useAuthStore();
  
  const {
    data: settingsData,
    isLoading,
    error,
    refetch
  } = useQuery('adminSettings', getSettings, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch settings');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    settingsData,
    isLoading,
    error,
    refetchSettings: refetch
  };
};

export const useUpdateSettings = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const updateSettingsMutation = useMutation(updateSettings, {
    onSuccess: () => {
      toast.success('Settings saved successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('adminSettings');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update settings';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  const updateSingleSettingMutation = useMutation(updateSetting, {
    onSuccess: (data, variables) => {
      toast.info(`Updated: ${variables.settingId}`, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('adminSettings');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update setting';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    updateSettings: updateSettingsMutation.mutate,
    updateSetting: updateSingleSettingMutation.mutate,
    isLoading: updateSettingsMutation.isLoading || updateSingleSettingMutation.isLoading,
    isError: updateSettingsMutation.isError || updateSingleSettingMutation.isError,
    error: updateSettingsMutation.error || updateSingleSettingMutation.error,
  };
};